import { productsApiUrl as apiUrl } from '../constants/endpoints';

class CartService {
  constructor() {
    this.sessionId = this.getSessionId();
  }

  getSessionId() {
    let sessionId = localStorage.getItem('cart_session_id');
    if (!sessionId) {
      sessionId = this.generateSessionId();
      localStorage.setItem('cart_session_id', sessionId);
    }
    return sessionId;
  }

  generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  async createCart() {
    try {
      const response = await fetch(`${apiUrl}/public_app/cart`, {
        method: 'POST',
        headers: {  
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to create cart');
      }

      const data = await response.json();
      this.sessionId = data.session_id;
      localStorage.setItem('cart_session_id', this.sessionId);
      return data;
    } catch (error) {
      console.error('Error creating cart:', error);
      throw error;
    }
  }

  async getCart() {
    try {
      const response = await fetch(`${apiUrl}/public_app/cart/${this.sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to get cart');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting cart:', error);
      throw error;
    }
  }

  async addToCart(productId, quantity = 1) {
    try {
      const response = await fetch(`${apiUrl}/public_app/cart/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: productId,
          quantity: quantity,
          session_id: this.sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();
      if (data.cart_session_id) {
        this.sessionId = data.cart_session_id;
        localStorage.setItem('cart_session_id', this.sessionId);
      }

      this.dispatchCartUpdateEvent();
      
      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  async updateQuantity(productId, quantity) {
    return this.addToCart(productId, quantity);
  }

  async removeFromCart(productId) {
    try {
      const response = await fetch(`${apiUrl}/public_app/cart/remove/${productId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: this.sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove from cart');
      }

      this.dispatchCartUpdateEvent();
      
      return await response.json();
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  }

  async createOrder(customerInfo) {
    try {
      const response = await fetch(`${apiUrl}/public_app/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: this.sessionId,
          ...customerInfo,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create order');
      }

      const data = await response.json();
      this.clearSession();
      this.dispatchCartUpdateEvent();
      
      return data;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  async getShippingConfig() {
    try {
      const response = await fetch(`${apiUrl}/public_app/shipping-config`);
      
      if (!response.ok) {
        throw new Error('Failed to get shipping config');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting shipping config:', error);
      throw error;
    }
  }

  clearSession() {
    localStorage.removeItem('cart_session_id');
    this.sessionId = this.generateSessionId();
    localStorage.setItem('cart_session_id', this.sessionId);

    this.dispatchCartUpdateEvent();
  }

  dispatchCartUpdateEvent() {
    const event = new CustomEvent('cartUpdated');
    window.dispatchEvent(event);
  }

  getCartItemCount() {
    return this.getCart().then(cart => cart.item_count || 0).catch(() => 0);
  }
}

export const cartService = new CartService();
